import { v4 as uuid } from 'uuid'

import { useCallback, useState } from 'react'

import {
  AdPage,
  AdPlatform,
  AdShape,
  getVanAdPlacement,
  VanPlacementConfigModel,
} from '@marketplace-web/domain/ads-data'
import { CountryCode } from '@marketplace-web/domain/country-data'

import { transformVanAdPlacement } from './utils'
import useVanAdLoadtimeLogging from '../useVanAdLoadtimeLogging'
import useRequestedAdLogging from '../useRequestedAdLogging'

type Props = {
  page: AdPage
  placementId: string
  countryCode: CountryCode
  platform: AdPlatform
  shape: AdShape
  consentStatus: boolean
}

const useFetchVanPlacement = ({
  page,
  placementId,
  countryCode,
  platform,
  shape,
  consentStatus,
}: Props) => {
  const [placement, setPlacement] = useState<VanPlacementConfigModel | null>(null)
  const { onVanAdLoadTimeRequest } = useVanAdLoadtimeLogging({
    countryCode,
    shape,
    page,
    platform,
  })
  const correlationId = uuid()

  const { onRequest: onRequestedAdRequest } = useRequestedAdLogging(placementId, correlationId)

  const fetchVanAdPlacement = useCallback(async () => {
    const fetchStartTime = Date.now()
    onRequestedAdRequest()
    const response = await getVanAdPlacement({ consentStatus, page, placementId, correlationId })

    const fetchEndTime = Date.now()
    const fetchTime = fetchEndTime - fetchStartTime

    const config = { page, placementId, fetchTime, platform, shape, countryCode, correlationId }

    if ('errors' in response || !response.ad) {
      setPlacement(transformVanAdPlacement(config))

      onVanAdLoadTimeRequest(placementId, fetchTime, false)

      return
    }

    setPlacement(transformVanAdPlacement(config, response.ad))

    onVanAdLoadTimeRequest(placementId, fetchTime, true)
  }, [
    consentStatus,
    page,
    placementId,
    platform,
    shape,
    countryCode,
    onVanAdLoadTimeRequest,
    onRequestedAdRequest,
    correlationId,
  ])

  return {
    placement,
    fetchVanAdPlacement,
  }
}

export default useFetchVanPlacement
